import React from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { formatCurrency } from 'ddc-intl';
import SingleItem from './SingleItem';
import './styles.scss';

const MAX_PACKAGES_DISPLAY = 3;

const ListStylePackages = ({
	packages,
	options,
	accessories,
	locale,
	onOpenModal,
	togglePackageDetails,
	prefs
}) => {
	const labels = useLabels();
	if (
		(!packages || packages.length === 0) &&
		(!options || options.length === 0) &&
		(!accessories || accessories.length === 0)
	) {
		return null;
	}

	const safePackages = packages || [];
	const safeOptions = options || [];
	const safeAccessories = accessories || [];
	const displayPackages = safePackages.slice(0, MAX_PACKAGES_DISPLAY);
	const hasMorePackages = safePackages.length > MAX_PACKAGES_DISPLAY;
	const totalItemsCount =
		displayPackages.length +
		(safeOptions.length > 0 ? 1 : 0) +
		safeAccessories.length;
	const totalOptionsPrice = safeOptions.reduce(
		(sum, item) => sum + (item.price || 0),
		0
	);
	const isCollapseEnabled = prefs?.toggleCollapse === 'true';
	const listStylePackagesClassName = isCollapseEnabled
		? 'list-style-packages list-style-packages-collapse-enabled'
		: 'list-style-packages';

	return (
		<div className={listStylePackagesClassName}>
			{displayPackages.map((pkg, index) => (
				<SingleItem
					key={pkg.name || index}
					index={index}
					itemName={pkg.name}
					subItems={pkg.subitems || []}
					itemType="package"
					itemPrice={
						pkg.price > 0 ? formatCurrency(pkg.price, locale) : '0'
					}
					onOpenModal={onOpenModal}
					totalItemsCount={totalItemsCount}
					isFirstItem={index === 0}
					isEnableDisclaimer={prefs?.enableDisclaimers === 'true'}
					layout={prefs?.packagesOptionsLayout || 'list'}
					togglePackageDetails={togglePackageDetails}
				/>
			))}
			{safeOptions.length > 0 && (
				<SingleItem
					itemType="option"
					itemName={labels.get('MORE_PRE_INSTALLED_ITEMS')}
					subItems={safeOptions}
					itemPrice={
						totalOptionsPrice > 0
							? formatCurrency(totalOptionsPrice, locale)
							: '0'
					}
					onOpenModal={onOpenModal}
					totalItemsCount={totalItemsCount}
					isFirstItem={displayPackages.length === 0}
					isEnableDisclaimer={prefs?.enableDisclaimers === 'true'}
					layout={prefs?.packagesOptionsLayout || 'list'}
					togglePackageDetails={togglePackageDetails}
				/>
			)}
			{safeAccessories.map((accessory, index) => (
				<SingleItem
					key={accessory.name || `accessory-${index}`}
					index={index}
					itemName={accessory.name}
					itemType="accessories"
					itemPrice={
						accessory.price > 0
							? formatCurrency(accessory.price, locale)
							: '0'
					}
					onOpenModal={onOpenModal}
					totalItemsCount={totalItemsCount}
					isFirstItem={
						displayPackages.length === 0 &&
						safeOptions.length === 0 &&
						index === 0
					}
					isEnableDisclaimer={prefs?.enableDisclaimers === 'true'}
					layout={prefs?.packagesOptionsLayout || 'list'}
					togglePackageDetails={togglePackageDetails}
				/>
			))}
			{hasMorePackages && (
				<div className="see-all-container py-4 mt-6">
					<button
						type="button"
						className="btn btn-link px-0"
						onClick={onOpenModal}
					>
						{labels.get('SEE_ALL_PACKAGES_OPTIONS')}
					</button>
				</div>
			)}
		</div>
	);
};

ListStylePackages.defaultProps = {
	packages: [],
	options: [],
	accessories: [],
	locale: 'en_US',
	togglePackageDetails: false,
	prefs: {}
};

ListStylePackages.propTypes = {
	packages: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired,
			subitems: PropTypes.arrayOf(
				PropTypes.shape({
					name: PropTypes.string,
					price: PropTypes.number
				})
			).isRequired
		})
	),
	options: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired,
			disclaimer: PropTypes.string
		})
	),
	accessories: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			price: PropTypes.number.isRequired
		})
	),
	locale: PropTypes.string,
	onOpenModal: PropTypes.func.isRequired,
	togglePackageDetails: PropTypes.bool,
	prefs: PropTypes.shape({
		displayPackagesPricing: PropTypes.string,
		enableDisclaimers: PropTypes.string,
		packagesOptionsLayout: PropTypes.string,
		toggleCollapse: PropTypes.string
	})
};

export default ListStylePackages;
